/**
 * DiaryProvider (web) — port of context/DiaryContext.tsx.
 * Real-time diary entries for the selected date, computed daily summary
 * (totals, carb-weighted avg GI, percentages, performance), debounced
 * dailySummaries snapshot writes, CRUD + streak updates, and the week strip's
 * performance dots.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { format, parseISO, addDays, subDays } from 'date-fns'
import { useAuth } from '@/auth/AuthProvider'
import { DiaryService, FirestoreService } from '@/services/firebase'
import { StreakService } from '@/services/streak'
import { getDefaultTargets } from '@/constants/glycemic-targets'
import { sumMicros } from '@/constants/micronutrients'
import type {
  DiaryEntry, DiaryEntryInput, DailySummary, DailyGlycemicTotals, DailyGlycemicTargets,
  DailyGlycemicPercentages, MealCounts, MealTime, DayPerformance,
} from '@/types/diary'

export interface DayPerformanceData { performance: DayPerformance; totalItems: number }

interface DiaryContextValue {
  selectedDate: string
  entries: DiaryEntry[]
  summary: DailySummary | null
  loading: boolean
  error: string | null
  setSelectedDate: (date: string) => void
  goToToday: () => void
  goToPreviousDay: () => void
  goToNextDay: () => void
  canGoToNextDay: boolean
  addEntry: (entry: DiaryEntryInput) => Promise<string>
  updateEntry: (entryId: string, updates: Partial<DiaryEntry>) => Promise<void>
  deleteEntry: (entryId: string) => Promise<void>
  copyDayEntries: (sourceDate: string, targetDate: string, targetMealType?: MealTime) => Promise<number>
  copyEntry: (entry: DiaryEntry, targetDate: string, targetMealType?: MealTime) => Promise<string>
  copyEntries: (entries: DiaryEntry[], targetDate: string, targetMealType?: MealTime) => Promise<number>
  refreshDiary: () => void
  getEntriesByMealType: (mealType: string) => DiaryEntry[]
  /** Week-strip dots: date → performance snapshot (dailySummaries). */
  weekPerformance: Record<string, DayPerformanceData>
  refreshWeek: (startDate: string, endDate: string) => Promise<void>
}

const DiaryContext = createContext<DiaryContextValue | undefined>(undefined)

export function todayKey(): string {
  return format(new Date(), 'yyyy-MM-dd')
}

/** Monday→Sunday week (date keys) containing the given date key. */
export function getWeekRange(dateKey: string): { start: string; end: string } {
  const date = parseISO(dateKey)
  const dow = date.getDay()
  const mondayOffset = dow === 0 ? -6 : 1 - dow
  const monday = addDays(date, mondayOffset)
  return { start: format(monday, 'yyyy-MM-dd'), end: format(addDays(monday, 6), 'yyyy-MM-dd') }
}

let _persistedDate: string = todayKey()

export function DiaryProvider({ children }: { children: ReactNode }) {
  const { user, userProfile } = useAuth()

  const [selectedDate, setSelectedDateState] = useState<string>(_persistedDate)
  const [entries, setEntries] = useState<DiaryEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [weekPerformance, setWeekPerformance] = useState<Record<string, DayPerformanceData>>({})
  const lastWrittenSummaryRef = useRef<Record<string, string>>({})

  // Midnight rollover: a tab left open overnight stays on yesterday. On focus,
  // advance only when the user was viewing the old "today".
  useEffect(() => {
    let lastToday = todayKey()
    const onFocus = () => {
      const now = todayKey()
      if (now !== lastToday) {
        setSelectedDateState((d) => (d === lastToday ? now : d))
        lastToday = now
      }
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)
    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onFocus)
    }
  }, [])

  useEffect(() => {
    if (!user) {
      setEntries([])
      setLoading(false)
      return
    }
    // Clear stale entries immediately so the previous day's totals can never
    // be computed (and persisted) against the newly selected date.
    setEntries([])
    setLoading(true)
    setError(null)
    return DiaryService.subscribeToEntriesForDate(
      user.uid,
      selectedDate,
      (fetched) => {
        setEntries(fetched)
        setLoading(false)
      },
      (err) => {
        console.error('Diary subscription error:', err)
        setError('Failed to load diary entries')
        setLoading(false)
      },
    )
  }, [user, selectedDate, refreshKey])

  const glycemicTargetsValue = userProfile?.glycemicTargets
  const diabetesType = userProfile?.diabetesProfile?.diabetesType
  const primaryGoal = userProfile?.diabetesProfile?.primaryGoal
  const optionalNutrients = userProfile?.optionalNutrients
  const targetsKey = JSON.stringify([glycemicTargetsValue, diabetesType, primaryGoal, optionalNutrients?.dailyCaloriesLimit, optionalNutrients?.dailyProteinLimit, optionalNutrients?.dailyFatLimit])

  const targets = useMemo((): DailyGlycemicTargets => {
    const base = glycemicTargetsValue ? { ...glycemicTargetsValue } : getDefaultTargets(diabetesType, primaryGoal)
    if (optionalNutrients?.dailyCaloriesLimit) base.calories = optionalNutrients.dailyCaloriesLimit
    if (optionalNutrients?.dailyProteinLimit) base.proteinG = optionalNutrients.dailyProteinLimit
    if (optionalNutrients?.dailyFatLimit) base.fatG = optionalNutrients.dailyFatLimit
    return base
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetsKey])

  const hasProfile = !!userProfile
  const summary = useMemo((): DailySummary | null => {
    if (!hasProfile) return null
    const baseTotals = entries.reduce(
      (acc, e) => ({
        glycemicLoad: acc.glycemicLoad + (e.nutrients.glycemicLoad || 0),
        carbsG: acc.carbsG + (e.nutrients.carbsG || 0),
        fiberG: acc.fiberG + (e.nutrients.fiberG || 0),
        sugarG: acc.sugarG + (e.nutrients.sugarG || 0),
        calories: acc.calories + (e.nutrients.calories || 0),
        proteinG: acc.proteinG + (e.nutrients.proteinG || 0),
        fatG: acc.fatG + (e.nutrients.fatG || 0),
      }),
      { glycemicLoad: 0, carbsG: 0, fiberG: 0, sugarG: 0, calories: 0, proteinG: 0, fatG: 0 },
    )
    const dailyMicros = sumMicros(entries.map((e) => e.nutrients.micros))

    let giWeightedSum = 0
    let giCarbWeightSum = 0
    for (const entry of entries) {
      if (entry.foods && entry.foods.length > 0) {
        const servings = entry.servingsConsumed || 1
        for (const food of entry.foods) {
          if (food.giValue != null && food.giValue >= 0 && food.carbsG != null && food.carbsG > 0) {
            const weightedCarbs = food.carbsG * servings
            giWeightedSum += food.giValue * weightedCarbs
            giCarbWeightSum += weightedCarbs
          }
        }
      } else if (entry.nutrients.giValue != null && entry.nutrients.giValue >= 0 && entry.nutrients.carbsG > 0) {
        giWeightedSum += entry.nutrients.giValue * entry.nutrients.carbsG
        giCarbWeightSum += entry.nutrients.carbsG
      }
    }
    const avgGIScore = giCarbWeightSum > 0 ? Math.round(giWeightedSum / giCarbWeightSum) : -1

    const totals: DailyGlycemicTotals = { ...baseTotals, ...(dailyMicros && { micros: dailyMicros }), avgGIScore, netCarbsG: Math.max(0, baseTotals.carbsG - baseTotals.fiberG) }

    const percentages: DailyGlycemicPercentages = {
      glycemicLoad: targets.glycemicLoad > 0 ? (totals.glycemicLoad / targets.glycemicLoad) * 100 : 0,
      avgGI: totals.avgGIScore >= 0 && targets.avgGIScore > 0 ? (totals.avgGIScore / targets.avgGIScore) * 100 : 0,
      netCarbs: targets.netCarbsG > 0 ? (totals.netCarbsG / targets.netCarbsG) * 100 : 0,
      fiber: targets.fiberG > 0 ? (totals.fiberG / targets.fiberG) * 100 : 0,
      sugar: targets.sugarG > 0 ? (totals.sugarG / targets.sugarG) * 100 : 0,
      calories: targets.calories && targets.calories > 0 ? ((totals.calories || 0) / targets.calories) * 100 : undefined,
      protein: targets.proteinG && targets.proteinG > 0 ? ((totals.proteinG || 0) / targets.proteinG) * 100 : undefined,
      fat: targets.fatG && targets.fatG > 0 ? ((totals.fatG || 0) / targets.fatG) * 100 : undefined,
    }

    const fiberTargetMet = percentages.fiber >= 100
    const overTargetNutrients: string[] = []
    if (percentages.glycemicLoad > 100) overTargetNutrients.push('glycemicLoad')
    if (percentages.netCarbs > 100) overTargetNutrients.push('netCarbs')
    if (percentages.sugar > 100) overTargetNutrients.push('sugar')

    const performance: DayPerformance = entries.length === 0 ? 'none' : overTargetNutrients.length === 0 && fiberTargetMet ? 'green' : percentages.glycemicLoad > 120 ? 'red' : 'yellow'

    const mealCounts: MealCounts = entries.reduce(
      (acc, e) => {
        if (e.mealType) acc[e.mealType as keyof MealCounts] = (acc[e.mealType as keyof MealCounts] || 0) + 1
        return acc
      },
      { breakfast: 0, lunch: 0, dinner: 0, snack: 0 },
    )

    return { date: selectedDate, totals, targets, percentages, mealCounts, performance, fiberTargetMet, hasOverTarget: overTargetNutrients.length > 0, overTargetNutrients }
  }, [entries, hasProfile, selectedDate, targets])

  // Persist the day's performance snapshot for the calendar dots (debounced, deduped).
  useEffect(() => {
    if (!user?.uid || !summary || loading) return
    if (summary.date !== selectedDate) return
    const payload = {
      totalItems: entries.length,
      glycemicLoad: summary.totals.glycemicLoad,
      avgGIScore: summary.totals.avgGIScore,
      netCarbsG: summary.totals.netCarbsG,
      carbsG: summary.totals.carbsG,
      fiberG: summary.totals.fiberG,
      sugarG: summary.totals.sugarG,
      performance: summary.performance,
    }
    const payloadKey = JSON.stringify(payload)
    if (lastWrittenSummaryRef.current[selectedDate] === payloadKey) return
    const timer = setTimeout(() => {
      FirestoreService.saveDailySummary(user.uid, selectedDate, payload)
        .then(() => {
          lastWrittenSummaryRef.current[selectedDate] = payloadKey
          setWeekPerformance((prev) => ({ ...prev, [selectedDate]: { performance: summary.performance, totalItems: entries.length } }))
        })
        .catch((err) => console.warn('Failed to update daily summary:', err))
    }, 500)
    return () => clearTimeout(timer)
  }, [user?.uid, selectedDate, summary, entries.length, loading])

  const setSelectedDate = useCallback((date: string) => {
    _persistedDate = date
    setSelectedDateState(date)
  }, [])
  const goToToday = useCallback(() => setSelectedDate(todayKey()), [setSelectedDate])
  const goToPreviousDay = useCallback(() => setSelectedDate(format(subDays(parseISO(selectedDate), 1), 'yyyy-MM-dd')), [selectedDate, setSelectedDate])
  const goToNextDay = useCallback(() => setSelectedDate(format(addDays(parseISO(selectedDate), 1), 'yyyy-MM-dd')), [selectedDate, setSelectedDate])

  const addEntry = useCallback(async (entry: DiaryEntryInput): Promise<string> => {
    if (!user) throw new Error('User not authenticated')
    const id = await DiaryService.createEntry({ ...entry, userId: user.uid })
    StreakService.updateStreak(user.uid).catch(() => {})
    return id
  }, [user])

  const updateEntry = useCallback(async (entryId: string, updates: Partial<DiaryEntry>) => DiaryService.updateEntry(entryId, updates), [])
  const deleteEntry = useCallback(async (entryId: string) => DiaryService.deleteEntry(entryId), [])

  const copyDayEntries = useCallback(async (sourceDate: string, targetDate: string, targetMealType?: MealTime) => {
    if (!user) throw new Error('User not authenticated')
    const n = await DiaryService.copyEntriesForDate(user.uid, sourceDate, targetDate, targetMealType)
    if (n > 0) StreakService.updateStreak(user.uid).catch(() => {})
    return n
  }, [user])

  const copyEntry = useCallback(async (entry: DiaryEntry, targetDate: string, targetMealType?: MealTime) => {
    if (!user) throw new Error('User not authenticated')
    const id = await DiaryService.copySingleEntry(entry, targetDate, targetMealType)
    StreakService.updateStreak(user.uid).catch(() => {})
    return id
  }, [user])

  const copyEntries = useCallback(async (list: DiaryEntry[], targetDate: string, targetMealType?: MealTime) => {
    if (!user) throw new Error('User not authenticated')
    const n = await DiaryService.copyMultipleEntries(list, targetDate, targetMealType)
    if (n > 0) StreakService.updateStreak(user.uid).catch(() => {})
    return n
  }, [user])

  const refreshDiary = useCallback(() => setRefreshKey((k) => k + 1), [])
  const getEntriesByMealType = useCallback((mealType: string) => entries.filter((e) => e.mealType === mealType), [entries])

  const refreshWeek = useCallback(async (startDate: string, endDate: string) => {
    if (!user) return
    try {
      const docs = await FirestoreService.getDailySummaries(user.uid, startDate, endDate)
      const next: Record<string, DayPerformanceData> = {}
      for (const [date, d] of Object.entries(docs)) {
        next[date] = { performance: (d.performance as DayPerformance) || 'none', totalItems: (d.totalItems as number) || 0 }
      }
      setWeekPerformance((prev) => ({ ...prev, ...next }))
    } catch (e) {
      console.error('Error fetching week summaries:', e)
    }
  }, [user])

  useEffect(() => {
    const { start, end } = getWeekRange(selectedDate)
    void refreshWeek(start, end)
  }, [selectedDate, refreshWeek])

  const value = useMemo<DiaryContextValue>(() => ({
    selectedDate, entries, summary, loading, error, setSelectedDate, goToToday, goToPreviousDay, goToNextDay, canGoToNextDay: true,
    addEntry, updateEntry, deleteEntry, copyDayEntries, copyEntry, copyEntries, refreshDiary, getEntriesByMealType, weekPerformance, refreshWeek,
  }), [selectedDate, entries, summary, loading, error, setSelectedDate, goToToday, goToPreviousDay, goToNextDay, addEntry, updateEntry, deleteEntry, copyDayEntries, copyEntry, copyEntries, refreshDiary, getEntriesByMealType, weekPerformance, refreshWeek])

  return <DiaryContext.Provider value={value}>{children}</DiaryContext.Provider>
}

export function useDiary(): DiaryContextValue {
  const ctx = useContext(DiaryContext)
  if (!ctx) throw new Error('useDiary must be used within a DiaryProvider')
  return ctx
}
